import React from 'react';
import { AlertTriangle, Calendar, MapPin, User } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { FallIncident } from '../../types/residents';

interface FallIncidentCardProps {
  incident: FallIncident;
}

export const FallIncidentCard: React.FC<FallIncidentCardProps> = ({ incident }) => {
  const observations = incident.observations;

  return (
    <div className="border border-orange-300 bg-white rounded-lg p-4">
      {/* Header: Fall Type + Date */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-bold text-[#D97706] mb-1">
            {incident.incident_type.description}
          </h3>
          <p className="text-xs text-gray-600 flex items-center gap-1">
            <Calendar className="w-3 h-3 shrink-0" />
            {formatDate(incident.incident_date)}
          </p>
          {incident.location?.description && (
            <p className="text-xs text-gray-600 flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3 shrink-0" />
              {incident.location.description}
              {incident.location.room_number && ` (Room: ${incident.location.room_number.trim()})`}
            </p>
          )}
        </div>
        {incident.observed_by && (
          <div className="text-right">
            <p className="text-[10px] text-gray-600 flex items-center justify-end gap-1">
              <User className="w-3 h-3" />
              Observed by
            </p>
            <p className="text-xs font-medium text-gray-800">{incident.observed_by}</p>
          </div>
        )}
      </div>

      {/* Observations Section */}
      {observations && (
        <div className="space-y-4 mb-3">
          {/* Minor Injuries */}
          {(observations.minor_injuries?.injury_types || observations.minor_injuries?.injury_locations) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {observations.minor_injuries.injury_types && observations.minor_injuries.injury_types.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    MINOR INJURIES
                  </h4>
                  <ul className="space-y-1">
                    {observations.minor_injuries.injury_types.map((injury, index) => (
                      <li key={index} className="text-sm text-gray-800 flex items-start">
                        <span className="mr-2 text-[#D97706]">&#8226;</span>
                        <span>{injury}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {observations.minor_injuries.injury_locations && observations.minor_injuries.injury_locations.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    MINOR INJURY LOCATIONS
                  </h4>
                  <ul className="space-y-1">
                    {observations.minor_injuries.injury_locations.map((location, index) => (
                      <li key={index} className="text-sm text-gray-800 flex items-start">
                        <span className="mr-2 text-[#D97706]">&#8226;</span>
                        <span>{location}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Major Injuries */}
          {(observations.major_injuries?.injury_types || observations.major_injuries?.injury_locations) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-200">
              {observations.major_injuries.injury_types && observations.major_injuries.injury_types.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold text-red-700 uppercase tracking-wide mb-2">
                    MAJOR INJURIES
                  </h4>
                  <ul className="space-y-1">
                    {observations.major_injuries.injury_types.map((injury, index) => (
                      <li key={index} className="text-sm text-gray-800 flex items-start">
                        <span className="mr-2 text-red-600">&#8226;</span>
                        <span className="font-medium">{injury}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {observations.major_injuries.injury_locations && observations.major_injuries.injury_locations.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold text-red-700 uppercase tracking-wide mb-2">
                    MAJOR INJURY LOCATIONS
                  </h4>
                  <ul className="space-y-1">
                    {observations.major_injuries.injury_locations.map((location, index) => (
                      <li key={index} className="text-sm text-gray-800 flex items-start">
                        <span className="mr-2 text-red-600">&#8226;</span>
                        <span className="font-medium">{location}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Psychological Symptoms */}
          {observations.psychological_symptoms && observations.psychological_symptoms.length > 0 && (
            <div className="pt-3 border-t border-gray-200">
              <h4 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">
                PSYCHOLOGICAL SYMPTOMS
              </h4>
              <ul className="space-y-1">
                {observations.psychological_symptoms.map((symptom, index) => (
                  <li key={index} className="text-sm text-gray-800 flex items-start">
                    <span className="mr-2 text-[#D97706]">&#8226;</span>
                    <span>{symptom}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Neurological Assessment */}
          {observations.neurological?.assessment_completed && (
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-semibold text-gray-800">
                  Neurological Assessment Completed
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer - Reported By */}
      {incident.reported_by && (
        <div className="pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            Reported by: <span className="font-medium text-gray-800">{incident.reported_by}</span>
          </p>
        </div>
      )}
    </div>
  );
};
